import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useSession } from "@/auth/contexts/auth-context.tsx";
import { useGetUserMe } from "@/auth/api/queries/user.queries.ts";
import { AUTH_URL } from "@/auth/routes.ts";
import { Logo } from "@/shared/components/common/logo";

const PersistLogin = () => {
  const { accessToken, refreshToken, isLoading: authLoading } = useSession();
  const { status, error } = useGetUserMe();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthPage = location.pathname.startsWith(AUTH_URL.SIGN) ||
                     location.pathname.startsWith(AUTH_URL.FORGOT_PASSWORD) ||
                     location.pathname.startsWith(AUTH_URL.ACCEPT_INVITE);

  useEffect(() => {
    console.log('[PersistLogin] Auth state:', {
      authLoading,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      queryStatus: status,
      isAuthPage,
      pathname: location.pathname,
    });

    // If we're on an auth page, don't do any auth checks
    if (isAuthPage) {
      console.log('[PersistLogin] On auth page, skipping checks');
      return;
    }

    // If auth context is done loading and no tokens exist, redirect to login
    if (!authLoading && !accessToken && !refreshToken) {
      console.log('[PersistLogin] No tokens found, redirecting to login');
      navigate(AUTH_URL.SIGN, { replace: true });
      return;
    }

    // If the user/me query failed (after token refresh attempt), redirect to login
    if (status === "error") {
      console.error('[PersistLogin] Authentication failed:', error);
      navigate(AUTH_URL.SIGN, { replace: true });
    }
  }, [authLoading, accessToken, refreshToken, status, error, navigate, isAuthPage, location.pathname]);

  // Show loading state while:
  // 1. Auth context is initializing from localStorage
  // 2. User verification query is pending
  const isLoading = authLoading || status === "pending";

  // If we're on an auth page, render it immediately
  if (isAuthPage) {
    return <Outlet />;
  }

  return (
    <AnimatePresence mode={'wait'}>
      {isLoading && (
        <motion.div
          key={"loading"}
          className={"flex items-center justify-center h-screen"}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Logo className="h-16 w-auto" />
        </motion.div>
      )}

      {!isLoading && status === "success" && (
        <motion.div
          key={'content'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Outlet />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PersistLogin;
